import { useEffect, useState } from "react"
import { decimalsOptions, ivaOptions, conceptOpt } from "helpers/dictionary"
import { formatNumber } from "helpers/getNumberFormats"
import { validateObject } from "helpers/validateObject"
import { useDispatch, useSelector } from "react-redux"
import { cleanNumber } from "helpers/cleanNumbers"
import { idGenerator } from "helpers/createId"
import { newDateWithTime, newDate } from "helpers/dateTools";
import {
  getNoteUnification,
  addCashInvoiceNote,
  updateDate,
  recalculateNotes,
  changeReason,
  cleanNoteUnification
} from "redux/actions/invoices/invoicesCashAction"
import { useForm } from "hooks/useForm"
import { initialValues, requiredValues } from "./inputValues"
import { useMediaQuery, useTheme } from "@mui/material"
import { setSnackbar } from "redux/actions/search/searchActions";
import { reasonNotesOptions, conceptLabels, discountTypes } from "./config"

const { discount: discountReason, advances } = reasonNotesOptions
const { liters } = discountTypes
const DEC2 = decimalsOptions[2]?.value;

export const useAddNotesHook = () => {

  const dispatch = useDispatch()
  const { breakpoints } = useTheme();
  const { noteUnification, notes, billingReasons, folioError, clearForm } = useSelector(({ cashInvoices }) => cashInvoices)
  const { isLoadingComp } = useSelector(({ search }) => search)

  const md = useMediaQuery(breakpoints.down("md"));
  const {
    values,
    errors,
    onChange,
    setValues,
    validate,
    onChangeNumber,
    reset,
    handleNewValues,
  } = useForm(initialValues.addNotes, requiredValues.addNotesRequired);

  const {
    id,
    idReason,
    date,
    folio,
    quantity,
    price,
    iva,
    decimals,
    invoiceKey,
    subTotalWithDiscount,
    totalWithDiscount,
  } = values;


  const [isDataChanged, setIsDataChanged] = useState(false)

  const handleGetNoteUnification = () => {
    if (notes?.length > 0 && notes.find(noteSave => noteSave?.folio === folio)) {
      dispatch(setSnackbar("No se puede repetir folio"))
    } else {
      dispatch(getNoteUnification(invoiceKey, folio))
    }
  }

  const dataSubmit = () => {
    let { block: _block, ...note } = values

    const nPrice = formatNumber(price) / (iva?.value + 1);

    const nPriceWithIva = formatNumber({
      input: price,
      decimals: decimals?.value
    })
    const nPriceWithoutIva = formatNumber({
      input: nPrice,
      decimals: decimals?.value
    })
    const ivaCalculate = formatNumber({
      input: cleanNumber(nPrice * quantity) * iva?.value,
      decimals: DEC2
    })
    let discount = {
      quantity: `${quantity}`,
      iva: ivaCalculate,
    }

    if (idReason?.description === discountReason) {
      discount = {
        subTotal: subTotalWithDiscount,
        iva: formatNumber({
          input: cleanNumber(subTotalWithDiscount) * iva?.value,
          decimals: DEC2
        }),
        total: totalWithDiscount,
      }
    }

    note = {
      ...note,
      ...discount,
      details: noteUnification?.detail && noteUnification?.unit ? noteUnification : null,
      ivaLabel: iva?.value,
      price: nPriceWithoutIva,
      priceWithIva: nPriceWithIva,
      id: id ? id : idGenerator(),
      idReason: idReason.value
    }
    dispatch(addCashInvoiceNote(note))
    setValues({
      id: "",
      folio: idReason?.description === advances || isDataChanged ? folio : "",
      price: price,
      quantity: 0,
      subTotal: 0,
      total: 0,
    })
  }

  const handleRecalculate = () => {
    const nPrice = formatNumber(price) / (iva?.value + 1);
    const nPriceWithoutIva = formatNumber({
      input: nPrice,
      decimals: decimals?.value
    })
    dispatch(recalculateNotes({
      date: date,
      price: nPriceWithoutIva,
      priceWithIva: formatNumber({ input: price, decimals: decimals?.value }),
      iva: iva,
      decimals: decimals
    }))
    setValues({
      price: price,
      quantity: 0,
      subTotal: 0,
      total: 0,
    })
  }
  const handleSubmit = (e) => {
    e.preventDefault()
    validate([], dataSubmit)
  }

  const handleChangeReason = (e) => {
    setValues(e)
    dispatch(changeReason(e.idReason?.description))
    if (e?.idReason?.description === advances) {
      setValues({ grouped: conceptOpt.find(concept => concept.label === conceptLabels.grouped), messageFolio: null })
    }
    if (e?.idReason?.description === discountReason) {
      dispatch(cleanNoteUnification())
      setValues({
        discount: 0,
        discountType: "",
        discountAmount: 0,
        quantity: 0,
        id: ''
      })
    }
  }

  const handleDate = ({ target }) => {
    const today = newDate();
    const nDate = target.value?.split("T")[0]

    if (nDate !== today) {
      handleNewValues({
        date: `${nDate}T23:59`,
      });
      dispatch(updateDate(`${nDate}T23:59`))
    } else {
      handleNewValues({
        date: target.value,
      })
      dispatch(updateDate(target.value))
    }
  };

  const handleDiscount = () => {
    const cleanPrice = cleanNumber(price)
    const discountVal = noteUnification?.customer?.discount || 0
    return formatNumber({
      input: cleanPrice > 0 && discountVal > 0 ? (quantity * discountVal) / cleanPrice : 0,
      decimals: DEC2
    })
  }

  useEffect(() => {
    const is72hrs = new Date().getDate()
    if (is72hrs > 0 && is72hrs < 4) setValues({ blockDate: false })
  }, [])

  useEffect(() => {
    !date && setValues({ date: newDateWithTime() })
  }, [date])

  useEffect(() => {
    if (validateObject(noteUnification)) {
      if (idReason?.description === discountReason ) {
        if (noteUnification?.customer?.discountType === liters && noteUnification?.customer?.discount > 0) {
          setValues({
            quantity: noteUnification?.quantity,
            id: noteUnification?.id,
            messageFolio: null,
            discount: noteUnification?.customer?.discount || 0,
            discountType: noteUnification?.customer?.discountType || "",
            discountAmount: formatNumber({ input: handleDiscount(), decimals: DEC2 }),
          })
        }
        else {
          dispatch(setSnackbar("Solo descuento en litros",'warning'))
          dispatch(cleanNoteUnification())
        }
      }
      else {
        setValues({
          quantity: noteUnification?.quantity,
          id: noteUnification?.id,
          messageFolio: null,
        })
      }
    }
    if (folioError) {
      setValues({ folio: "", messageFolio: "Folio no encontrado" })
    }
  }, [noteUnification, folioError,])

  useEffect(() => {
    if (idReason?.description === advances || idReason?.description === reasonNotesOptions.foreign || id) {
      setValues({ block: false })
    } else {
      setValues({ block: true })
    }
  }, [idReason, id])

  useEffect(() => {
    if (decimals === null) {
      setValues({
        decimals: decimalsOptions[0],
      });
    } else {
      setValues({
        price: formatNumber({
          input: price,
          decimals: decimals?.value
        })
      })
    }
  }, [decimals]);

  useEffect(() => {
    iva === null && setValues({ iva: ivaOptions[0] })
  }, [iva])

  const calculateTotals = (price, quantity, iva, decimals) => {
    const priceWithoutIVA = Number((cleanNumber(price) / (iva?.value + 1)).toFixed(decimals?.value));
    const subT = cleanNumber(priceWithoutIVA) * cleanNumber(quantity) || priceWithoutIVA * quantity;
    const currentIva = cleanNumber(subT * iva?.value);
    const total = cleanNumber(subT + currentIva);
    return { priceWithoutIVA, subT, currentIva, total };
  };

  const calculateDiscount = (priceWithoutIVA, quantity, iva) => {
    const discountAmount = handleDiscount();
    const quantityWithDiscount = Math.max(0, Number((cleanNumber(quantity) - discountAmount).toFixed(DEC2)));
    const subTotalWithDiscount = Number((cleanNumber(priceWithoutIVA) * quantityWithDiscount).toFixed(DEC2));
    const ivaWithDiscount = Number((subTotalWithDiscount * iva?.value).toFixed(DEC2));
    const totalWithDiscount = Number((subTotalWithDiscount + ivaWithDiscount).toFixed(DEC2));
    return { discountAmount, subTotalWithDiscount, totalWithDiscount };
  };

  const checkDataChanged = (notes, price, decimals, iva, idReason) => {
    if (notes.length === 0) return false;
    return (
      idReason?.description === advances ||
      idReason?.description === reasonNotesOptions.buildings ||
      notes[0].priceWithIva !== formatNumber({ input: price, decimals: decimals?.value }) ||
      notes[0].decimals?.value !== decimals?.value ||
      notes[0].ivaLabel !== iva?.value
    );
  };

  useEffect(() => {
    const { priceWithoutIVA, subT, total } = calculateTotals(price, quantity, iva, decimals);

    if (idReason?.description === discountReason && noteUnification?.customer?.discount > 0) {
      const discount = calculateDiscount(priceWithoutIVA, quantity, iva);
      setValues({
        discountAmount: formatNumber({ input: discount.discountAmount, decimals: DEC2 }),
        subTotalWithDiscount: formatNumber({ input: discount.subTotalWithDiscount, decimals: DEC2 }),
        totalWithDiscount: formatNumber({ input: discount.totalWithDiscount, decimals: DEC2 }),
      });
    }

    setValues({
      subTotal: formatNumber({ input: subT, decimals: DEC2 }),
      total: formatNumber({ input: total, decimals: DEC2 }),
    });

    setIsDataChanged(checkDataChanged(notes, price, decimals, iva, idReason));
  }, [quantity, price, iva, decimals, notes])

  useEffect(() => {
    if (clearForm) {
      reset()
    }
  }, [clearForm])

  return {
    handleGetNoteUnification,
    handleRecalculate,
    handleSubmit,
    isDataChanged,
    values, notes, md,
    onChange, onChangeNumber, errors, setValues,
    isLoadingComp, billingReasons,
    handleChangeReason,
    handleDate,
  }
}